import { Extension, ReaderContext, WriterContext } from '@gltf-transform/core3';
import { CYNCLY_LIGHTS_METADATA } from '../constants';
import { LightMetadata } from './light-metadata';

const NAME = CYNCLY_LIGHTS_METADATA;

interface LightMetadataDef {

    /**
     * The type of the light.
     */
    type: LightType;

    /**
     * Measure of how much light is emitted by this source in Lumens.
     */
    intensity: number | null;

    /**
     * RGB value for light's color in linear space.
     * @defaultValue [1, 1, 1]
     */
    color: [number, number, number] | null;

    /**
     * The beam angle in degrees. Also known as apex angle. Specific to light type `spot`
     */
    beamAngle: number | null;
}

export class CYNCLYLightsMetadata extends Extension {
	public readonly extensionName = NAME;
	public static readonly EXTENSION_NAME = NAME;

	/** Creates a new LightMetadata property for use on a {@link Node}. */
	public createLightMetadata(name = ''): LightMetadata {
		return new LightMetadata(this.document.getGraph(), name);
	}

	public read(context: ReaderContext): this {
		const jsonDoc = context.jsonDoc;

		jsonDoc.json.nodes!.forEach((nodeDef, nodeIndex) => {
			if (!nodeDef.extensions || !nodeDef.extensions[NAME]) return;
			const lightMetadataDef = nodeDef.extensions[NAME] as LightMetadataDef;
			const lightMetadata = this.createLightMetadata()
			lightMetadata.setType(lightMetadataDef.type);
			lightMetadata.setIntensity(lightMetadataDef.intensity);
			lightMetadata.setColor(lightMetadataDef.color);
			lightMetadata.setBeamAngle(lightMetadataDef.beamAngle);

			context.nodes[nodeIndex].setExtension(NAME, lightMetadata);
		});

		return this;
	}

	public write(context: WriterContext): this {
		if (this.properties.size === 0) return this;

		this.document
			.getRoot()
			.listNodes()
			.forEach((node) => {
				const lightMetadata = node.getExtension<LightMetadata>(NAME);
				if (lightMetadata) {
					const lightMetadataDef = {} as LightMetadataDef;

					if (lightMetadata.getType() != null)
						lightMetadataDef.type = lightMetadata.getType();
					if (lightMetadata.getIntensity() != null)
						lightMetadataDef.intensity = lightMetadata.getIntensity();
					if (lightMetadata.getColor() != null)
						lightMetadataDef.color = lightMetadata.getColor() as [number, number, number];
					if (lightMetadata.getBeamAngle() != null)
						lightMetadataDef.beamAngle = lightMetadata.getBeamAngle();

					const nodeIndex = context.nodeIndexMap.get(node)!;
					const nodeDef = context.jsonDoc.json.nodes![nodeIndex];
					nodeDef.extensions = nodeDef.extensions || {};
					nodeDef.extensions[NAME] = lightMetadataDef;
				}
			});

		return this;
	}
}
